import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as GroupCatalogsActions from './group-catalogs.actions';
import { GroupCatalogsStoreService } from './group-catalogs.service';
import { ArkikAlertService } from 'src/app/shared/components/arkik-alert/arkik-alert.service';
import { TranslationService } from 'src/app/core/services/common/translation.service';
import { map, switchMap, catchError, mergeMap, take, timeout, takeUntil } from 'rxjs/operators';
import { defer, of, from, merge } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectGroupsCatalog } from './group-catalogs.selectors';
import { ManageIdbService } from 'src/app/idb/manage-idb.service';
import { IdbCatalogName } from 'src/app/idb/idb-catalog-name.enum';
import { CATALOG_OPERATION_TIMEOUT_MS, reportCatalogError } from '@core/services/common/catalog-operation';
import { loadLogin, logout, resetLogin } from '../login/login.actions';

@Injectable()
export class GroupCatalogsEffects {
  constructor(
    private actions$: Actions,
    private groupCatalogsService: GroupCatalogsStoreService,
    private alert: ArkikAlertService,
    private translation: TranslationService,
    private store: Store,
    private idb: ManageIdbService,
  ) {}

  loadGroupsCatalog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupCatalogsActions.loadGroupsCatalog),
      mergeMap((request) =>
        defer(() => this.idb.prepareCatalogAccess(this.idb.catalogEpoch)).pipe(
          switchMap(() => this.store.select(selectGroupsCatalog)),
          take(1),
          switchMap((groups) => {
            const epoch = this.idb.catalogEpoch;
            return defer(() =>
              Promise.all([this.idb.select(IdbCatalogName.Groups), this.idb.select(IdbCatalogName.SubGroups)]),
            ).pipe(
              switchMap(([cachedGroups, cachedSubGroups]) => {
                if (groups?.length) {
                  return cachedGroups?.length && cachedSubGroups?.length
                    ? of(groups)
                    : from(this.idb.saveGroupsCatalog(groups, epoch)).pipe(map(() => groups));
                }
                if (cachedGroups?.length) {
                  return cachedSubGroups?.length
                    ? of(cachedGroups)
                    : from(this.idb.saveGroupsCatalog(cachedGroups, epoch)).pipe(map(() => cachedGroups));
                }
                return this.groupCatalogsService
                  .getGroupsCatalog()
                  .pipe(
                    switchMap((response) =>
                      from(this.idb.saveGroupsCatalog(response, epoch)).pipe(map(() => response)),
                    ),
                  );
              }),
            );
          }),
          timeout(CATALOG_OPERATION_TIMEOUT_MS),
          map((groups) => GroupCatalogsActions.loadGroupsCatalogSuccess({ ...request, groups })),
          catchError((error: unknown) =>
            of(
              GroupCatalogsActions.loadGroupsCatalogFailure({
                ...request,
                error: reportCatalogError(
                  this.alert,
                  'groups',
                  request,
                  error,
                  this.translation.pt('views.globals.an-error-occurred'),
                  this.translation.pt('views.globals.reload'),
                ),
              }),
            ),
          ),
          takeUntil(merge(this.idb.catalogCancellation$, this.actions$.pipe(ofType(loadLogin, logout, resetLogin)))),
        ),
      ),
    ),
  );

  refreshGroupsCatalog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupCatalogsActions.refreshGroupsCatalog),
      map(() => GroupCatalogsActions.resetGroupsCatalog()),
    ),
  );
}
